<template>
  <v-dialog :value="value" max-width="480" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="subtitle-1">{{ $tt('feed.shareTitle') }}</v-card-title>
      <v-card-text>
        <v-select v-model="visibility" :items="visibilityItems" :label="$tt('feed.shareVisibility')" dense outlined />
        <!-- 経路スイッチは走行記録 (session_id あり) の共有時のみ -->
        <v-switch v-if="hasRoute" v-model="routeVisible" :label="$tt('feed.shareRoute')" dense class="mt-0" />
        <v-switch
          v-if="hasRoute && routeVisible"
          v-model="locationExact"
          :label="$tt('feed.shareExact')"
          dense
          class="mt-0"
        />
        <v-textarea v-model="text" :label="$tt('feed.shareComment')" counter="500" rows="3" outlined dense />
        <!-- 記録から写真を引き継ぐ場合 (地点メモ等) は写真入力を出さない -->
        <v-file-input
          v-if="!hidePhotoInput && (!initialPhotoIds || initialPhotoIds.length === 0)"
          v-model="photoFiles"
          :label="$tt('feed.sharePhotos')"
          accept="image/*"
          multiple
          dense
          outlined
          prepend-icon="mdi-camera"
          :hint="$tt('feed.sharePhotosHint')"
          persistent-hint
          class="mb-2"
        />
        <p class="caption grey--text mb-2">{{ $tt('feed.consentNote') }}</p>
        <v-checkbox v-if="needConsent" v-model="consentAccepted" :label="$tt('feed.consentLabel')" dense class="mt-0" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">{{ $tt('customCancel') }}</v-btn>
        <v-btn color="primary" :loading="posting" :disabled="needConsent && !consentAccepted" @click="submit()">
          {{ $tt('feed.shareSubmit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UserInfo from '@/lib/user-info'
import feedApiClient from '@/lib/feed-api-client'
import Toast from '@/lib/toast'

export default {
  name: 'SharePostDialog',
  props: {
    value: { type: Boolean, default: false },
    taskMeta: { type: Object, default: null }, // { operation_type, started_at, duration_min }
    initialPhotoIds: { type: Array, default: () => [] }, // 手動記録などアップロード済み写真の引き継ぎ
    initialText: { type: String, default: '' }, // 地点メモ共有などの本文プリフィル
    cellOverride: { type: Object, default: null }, // { la, lo } メッシュセル (地点メモは POI 座標から算出)
    hidePhotoInput: { type: Boolean, default: false }, // 地点メモ共有など写真を記録から引き継ぐ場合は入力欄を出さない
  },
  data() {
    return {
      visibility: localStorage.getItem('feed.defaultVisibility') || 'followers',
      routeVisible: false,
      locationExact: false,
      text: '',
      photoFiles: [],
      needConsent: false,
      consentAccepted: false,
      posting: false,
    }
  },
  watch: {
    value(v) {
      if (v && this.initialText && !this.text) {
        this.text = this.initialText.slice(0, 500)
      }
    },
  },
  computed: {
    hasRoute() {
      return !!(this.taskMeta && this.taskMeta.session_id)
    },
    visibilityItems() {
      return [
        { value: 'private', text: this.$tt('feed.visPrivate') },
        { value: 'followers', text: this.$tt('feed.visFollowers') },
        { value: 'neighborhood', text: this.$tt('feed.visNeighborhood') },
        { value: 'public', text: this.$tt('feed.visPublic') },
      ]
    },
  },
  methods: {
    async submit() {
      if (this.text.length > 500) return
      this.posting = true
      try {
        let cell = this.cellOverride
        if (!cell) {
          try {
            cell = JSON.parse(localStorage.getItem('feed.cell'))
          } catch (e) {
            cell = null
          }
        }
        // 写真を先にアップロードして photo_id を得る (引き継ぎ分と合わせて最大4枚)
        const photoIds = (this.initialPhotoIds || []).slice(0, 4)
        for (const f of (this.photoFiles || []).slice(0, 4 - photoIds.length)) {
          const res = await feedApiClient.uploadPhoto(f)
          photoIds.push(res.data.id)
        }
        const body = {
          visibility: this.visibility,
          route_visible: this.hasRoute && this.routeVisible,
          location_exact: this.hasRoute && this.routeVisible && this.locationExact,
          text: this.text,
          cell: cell || undefined,
          task: this.taskMeta || undefined,
          author_nickname: UserInfo.getNickname(),
          photo_ids: photoIds.length ? photoIds : undefined,
        }
        if (this.needConsent && this.consentAccepted) {
          body.consent_accepted = true
        }
        await feedApiClient.createPost(body)
        localStorage.setItem('feed.defaultVisibility', this.visibility)
        Toast.i18nSuccess('feed.sharePosted')
        this.text = ''
        this.photoFiles = []
        this.$emit('input', false)
        this.$emit('posted')
      } catch (e) {
        if (e.response && e.response.status === 428) {
          // 初回共有: 規約第15条の2 への明示同意が必要
          this.needConsent = true
        } else {
          console.error(e.response || e)
          Toast.i18nError('feed.shareFailed')
        }
      } finally {
        this.posting = false
      }
    },
  },
}
</script>
